// src/pages/Users.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { AdminUserDto } from '../types/dtos';
import LoadingSpinner from '../components/LoadingSpinner';
import Sidebar from '../components/Sidebar';
import { Plus, Eye, X, Shield } from 'lucide-react';

export default function Users() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null); // To check if super admin
  
  // Reset Password Modal State
  const [showResetModal, setShowResetModal] = useState(false);
  const [resettingUser, setResettingUser] = useState<AdminUserDto | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      setError(null);
      try {
        // ✅ Fetch current user for role check
        const profileRes = await apiClient.get('/admin/profile');
        setCurrentUser(profileRes.data);

        // ✅ Fetch admin users
        const res = await apiClient.get<AdminUserDto[]>('/admin/users');
        setUsers(res.data);
      } catch (err: any) {
        console.error('[Users] Failed to load data:', err);
        setError(err.response?.data?.message || 'Unable to load users.');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const isSuperAdmin = currentUser?.company === "Xtrem Group";

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resettingUser) return;
    
    setResetLoading(true);
    setResetError(null);
    setResetSuccess(null);
    
    try {
      await apiClient.post(`/admin/users/${resettingUser.id}/reset-password`, { newPassword });
      setResetSuccess(`Password for ${resettingUser.firstName} reset successfully.`);
      setNewPassword('');
      setTimeout(() => {
        setShowResetModal(false);
        setResetSuccess(null);
      }, 2000);
    } catch (err: any) {
      setResetError(err.response?.data?.message || "Failed to reset password.");
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto w-full pt-4">


          {/* PRD Section 4: "User Management" - Header Card */}
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6 border border-gray-100">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">User Management</h1>
                <p className="mt-1 text-sm text-gray-500">View, create, edit, and deactivate users</p>
              </div>
              <button
                onClick={() => navigate('/users/new')} // Navigate to new user form
                className="w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-blue-700 to-indigo-800 text-white rounded-lg text-sm font-medium hover:from-blue-800 hover:to-indigo-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-md flex items-center justify-center transition-all"
                aria-label="Create a new user"
              >
                <Plus size={18} className="mr-2" />
                <span>Create User</span>
              </button>
            </div>
          </div>

          {/* PRD Section 4: "User Management" - Users Table */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            {loading ? (
              <div className="flex justify-center items-center min-h-[200px]" aria-live="polite">
                <LoadingSpinner />
              </div>
            ) : error ? (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 text-red-700" role="alert">
                {error}
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-gray-600">No users found.</p>
                <p className="text-gray-600 mt-2">Create a new user to get started.</p>
                <button
                  onClick={() => navigate('/users/new')}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  Create User
                </button>
              </div>
            ) : (
              <div className="bg-white">
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Name</th>
                        <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Email</th>
                        <th scope="col" className="px-6 py-4 text-left text-xs uppercase tracking-wider">Company</th>
                        <th scope="col" className="px-6 py-4 text-left text-xs uppercase tracking-wider">Roles</th>
                        <th scope="col" className="px-6 py-4 text-left text-xs uppercase tracking-wider">Status</th>
                        <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Last Login</th>
                        <th scope="col" className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {users.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{user.firstName} {user.lastName}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-medium">{user.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">{user.company || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-wrap gap-1">
                              {user.roles && user.roles.length > 0 ? (
                                user.roles.map(role => (
                                  <span key={role} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold rounded border border-blue-100">
                                    {role}
                                  </span>
                                ))
                              ) : (
                                <span className="text-gray-400 text-xs italic">No roles</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2.5 py-1 inline-flex text-[10px] font-bold uppercase rounded-full border ${user.isActive ? 'bg-green-50 text-green-700 border-green-100' : 'bg-red-50 text-red-700 border-red-100'
                              }`}>
                              {user.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                            {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                            <button
                              onClick={() => navigate(`/users/${user.id}`)}
                              className="text-blue-600 hover:text-blue-800 font-bold"
                              aria-label={`View details for ${user.firstName} ${user.lastName}`}
                            >
                              Details
                            </button>
                            {isSuperAdmin && (
                                <button
                                    onClick={() => {
                                        setResettingUser(user);
                                        setShowResetModal(true);
                                    }}
                                    className="ml-4 text-amber-600 hover:text-amber-800 font-bold"
                                    aria-label={`Reset password for ${user.firstName} ${user.lastName}`}
                                >
                                    Reset Pass
                                </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden divide-y divide-gray-100">
                  {users.map((user) => (
                    <div key={user.id} className="p-4 bg-white hover:bg-gray-50 transition-colors">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-sm font-bold shadow-sm">
                            {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900">{user.firstName} {user.lastName}</h3>
                            <p className="text-[10px] text-gray-400 font-bold uppercase">{user.company || 'No Company'}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(`/users/${user.id}`)}
                          className="p-2 text-blue-600 bg-blue-50 rounded-lg"
                        >
                          <Eye size={18} />
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-3 mb-2">
                        <div>
                          <p className="text-[9px] text-gray-400 font-bold uppercase">Status</p>
                          <span className={`inline-block px-2 py-0.5 rounded text-[9px] font-bold uppercase ${user.isActive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="text-right">
                          <p className="text-[9px] text-gray-400 font-bold uppercase">Last Login</p>
                          <p className="text-[10px] text-gray-600 font-medium truncate">
                            {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Reset Password Modal */}
          {showResetModal && resettingUser && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm shadow-2xl">
                  <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden transform transition-all border border-gray-100">
                      <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-4 flex items-center justify-between text-white shadow-md">
                          <h3 className="text-lg font-bold uppercase tracking-tight">Security: Reset Password</h3>
                          <button onClick={() => setShowResetModal(false)} className="text-white hover:text-amber-100 transition-colors">
                              <X size={24} />
                          </button>
                      </div>
                      
                      <form onSubmit={handleResetPassword} className="p-6 space-y-5">
                          <div>
                              <p className="text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">User Account</p>
                              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100 shadow-inner">
                                  <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold shadow-sm">
                                      {resettingUser.firstName.charAt(0)}{resettingUser.lastName.charAt(0)}
                                  </div>
                                  <div>
                                      <p className="font-bold text-gray-900 leading-tight">{resettingUser.firstName} {resettingUser.lastName}</p>
                                      <p className="text-xs text-gray-500 font-medium">{resettingUser.email}</p>
                                  </div>
                              </div>
                          </div>

                          <div>
                              <label className="block text-xs font-bold text-gray-700 uppercase mb-2 tracking-widest">Define New Password</label>
                              <input
                                  type="text"
                                  autoFocus 
                                  required
                                  placeholder="Enter new strong password"
                                  className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-0 text-sm font-mono transition-all shadow-sm placeholder:text-gray-400"
                                  value={newPassword}
                                  onChange={e => setNewPassword(e.target.value)}
                              />
                              <p className="mt-2 text-[10px] text-gray-400 flex items-center italic">
                                  <Shield size={10} className="mr-1" /> Override existing password directly
                              </p>
                          </div>

                          {resetError && <p className="p-3 bg-red-50 text-red-600 rounded-lg text-xs font-bold border border-red-100 animate-shake">{resetError}</p>}
                          {resetSuccess && <p className="p-3 bg-green-50 text-green-600 rounded-lg text-xs font-bold border border-green-100 animate-pulse">{resetSuccess}</p>}

                          <div className="flex gap-3 pt-2">
                              <button
                                  type="button"
                                  onClick={() => setShowResetModal(false)}
                                  className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-50 transition-all uppercase tracking-wider"
                              >
                                  Cancel
                              </button>
                              <button
                                  type="submit"
                                  disabled={resetLoading}
                                  className="flex-2 px-8 py-3 bg-gradient-to-r from-amber-600 to-orange-700 text-white rounded-xl text-sm font-bold hover:shadow-lg disabled:opacity-50 transition-all uppercase tracking-widest shadow-md active:scale-95"
                              >
                                  {resetLoading ? 'Processing...' : 'Confirm Reset'}
                              </button>
                          </div>
                      </form>
                  </div>
              </div>
          )}
        </main>
    </>
  );
}